/**
 * Theme → Appearance and Theme → Reading (the prototype's `settingsEditor()` Theme branches; web.md §4,
 * §7). Appearance: the theme cards — the five predefined themes and Custom, those the plan does not
 * include marked Pro — accent and density. Reading: text size, show images, mark read on open.
 *
 * Both edit a draft that previews live and is stored with Save (`settings.set`). Choosing a theme the
 * plan does not include is allowed in the form; the engine refuses it on Save (`not_entitled`) and
 * `reportError` opens the upsell — the UI never re-implements the limit.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PREDEFINED_THEME_IDS, type AppearanceSettings, type Entitlement, type ReadingSettings, type SyncedSettings, type ThemeId } from '@digester/core';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { themeLocked } from '../../theme/apply.js';
import { THEME_PRESETS } from '../../theme/presets.js';
import { Field, Section, Select, Toggle } from '../../ui/form.js';
import { ProMark } from '../../account/shared.js';
import { EditorFrame, useDraft } from '../shell/index.js';
import { useAppearancePreview } from './preview.js';

const THEME_CHOICES: readonly ThemeId[] = [...PREDEFINED_THEME_IDS, 'custom'];

function themeName(id: ThemeId): string {
  return id === 'custom' ? 'Custom' : THEME_PRESETS[id].name;
}

export interface ThemeEditorProps {
  settings: SyncedSettings;
  entitlement: Entitlement;
}

/** Store a synced setting and put the answer in the cache at once (no flash of the old value). */
function useSaveSetting() {
  const engine = useEngine();
  const queryClient = useQueryClient();
  return async <K extends 'appearance' | 'reading' | 'customTheme'>(key: K, value: SyncedSettings[K]): Promise<void> => {
    const next = await engine.settings.set(key, value);
    queryClient.setQueryData(qk.settings(), next);
  };
}

export function AppearanceEditor({ settings, entitlement }: ThemeEditorProps) {
  const navigate = useNavigate();
  const save = useSaveSetting();
  const draft = useDraft<AppearanceSettings>({ initial: settings.appearance, onSave: (value) => save('appearance', value) });
  const saved = { appearance: settings.appearance, reading: settings.reading, customTheme: settings.customTheme, limits: entitlement.limits };
  useAppearancePreview(saved, { ...saved, appearance: draft.value }, draft.dirty);
  const set = <K extends keyof AppearanceSettings>(key: K, value: AppearanceSettings[K]): void => draft.set((d) => ({ ...d, [key]: value }));

  return (
    <EditorFrame title="Appearance" icon="sun">
      <Section title="Theme">
        <div className="theme-options" role="radiogroup" aria-label="Theme">
          {THEME_CHOICES.map((id) => {
            const locked = themeLocked(id, entitlement.limits);
            return (
              <label key={id} className={locked ? 'theme-option locked' : 'theme-option'} data-theme-choice={id}>
                <input type="radio" name="theme" value={id} checked={draft.value.theme === id} onChange={() => set('theme', id)} />
                <span className={`theme-preview ${id}`}>
                  <i />
                  <i />
                  <i />
                </span>
                <strong>
                  {themeName(id)}
                  {locked ? <ProMark title={`${themeName(id)} is included in Pro`} /> : null}
                </strong>
              </label>
            );
          })}
        </div>
        <button type="button" className="text-link" onClick={() => navigate('/settings/theme/custom')}>
          <Icon name="palette" /> Edit custom theme <Icon name="next" />
        </button>
      </Section>
      <Section title="Accent">
        <Select
          label="Accent colour"
          name="accent"
          value={draft.value.accent}
          options={[
            ['violet', 'Violet'],
            ['blue', 'Blue'],
            ['green', 'Green'],
            ['neutral', 'Neutral'],
          ]}
          onChange={(accent) => set('accent', accent)}
        />
      </Section>
      <Section title="Density">
        <Select
          label="Message list"
          name="density"
          value={draft.value.density}
          options={[
            ['comfortable', 'Comfortable'],
            ['compact', 'Compact'],
          ]}
          onChange={(density) => set('density', density)}
        />
      </Section>
    </EditorFrame>
  );
}

export function ReadingEditor({ settings, entitlement }: ThemeEditorProps) {
  const save = useSaveSetting();
  const draft = useDraft<ReadingSettings>({ initial: settings.reading, onSave: (value) => save('reading', { ...value, fontSize: Math.round(value.fontSize) }) });
  const saved = { appearance: settings.appearance, reading: settings.reading, customTheme: settings.customTheme, limits: entitlement.limits };
  const previewSize = Number.isFinite(draft.value.fontSize) ? Math.min(28, Math.max(15, draft.value.fontSize)) : settings.reading.fontSize;
  useAppearancePreview(saved, { ...saved, reading: { ...draft.value, fontSize: previewSize } }, draft.dirty);

  return (
    <EditorFrame title="Reading" icon="eye">
      <Section title="Reading">
        <Field
          label="Text size · px"
          name="fontSize"
          type="number"
          min={15}
          max={28}
          required
          value={Number.isFinite(draft.value.fontSize) ? draft.value.fontSize : ''}
          onChange={(v) => draft.set((d) => ({ ...d, fontSize: v === '' ? Number.NaN : Number(v) }))}
        />
        <Toggle label="Show images" name="images" checked={draft.value.showImages} onChange={(showImages) => draft.set((d) => ({ ...d, showImages }))} />
        <Toggle label="Mark read on open" name="readOnOpen" checked={draft.value.readOnOpen} onChange={(readOnOpen) => draft.set((d) => ({ ...d, readOnOpen }))} />
      </Section>
    </EditorFrame>
  );
}
